import React, { useState, useEffect, useCallback } from 'react';
import { invoiceService } from '../services/api';
import { ADMIN_OPS_REFRESH } from '../constants/adminEvents';
import './InvoicesManagement.css';

/** Factura fuera de pendiente/rechazo y sin Numbers limpio → mostrar “Reenviar a Numbers”. */
function invoiceNeedsNumbersResend(inv) {
  if (!inv || inv.status === 'pending_review' || inv.status === 'rejected') {
    return false;
  }
  const hasId =
    inv.numbers_invoice_id != null && String(inv.numbers_invoice_id).trim() !== '';
  const hasErr =
    inv.numbers_sync_error != null && String(inv.numbers_sync_error).trim() !== '';
  return !hasId || hasErr;
}

function InvoiceDetailBody({ data, formatCurrency, formatDate, getStatusBadge }) {
  const { invoice, order, order_items, fiscal_preview, fiscal_preview_error } = data;
  const inv = invoice;
  const o = order;
  const lineItems = fiscal_preview?.items?.length
    ? fiscal_preview.items
    : (order_items || []).map((it) => ({
        name: it.product_name || it.name || 'Producto',
        quantity: it.quantity,
        unitPrice: Number(it.product_price || it.price || 0) || 0,
        subtotal: Number(it.subtotal || 0) || 0
      }));
  const totals = fiscal_preview?.totals || {
    subtotal: Number(o.subtotal) || 0,
    tax: Number(o.tax) || 0,
    shipping: Number(o.shipping) || 0,
    total: Number(o.total) || 0
  };
  const tp = fiscal_preview?.thirdParty;
  const useDiff = !!o.use_different_billing_address;
  const hasBillingName = !!(o.billing_customer_name && String(o.billing_customer_name).trim());

  return (
    <div className="invoice-detail-body">
      <div className="invoice-detail-title-row">
        <p className="invoice-detail-meta">
          <strong>Factura interna</strong> #{inv.id} · <strong>Orden</strong> {o.order_number}
        </p>
        {getStatusBadge(inv.status)}
      </div>
      {inv.numbers_invoice_id ? (
        <p className="invoice-detail-numbers">
          <strong>Referencia Numbers / nº externo:</strong> {String(inv.numbers_invoice_id)}
        </p>
      ) : null}
      {inv.numbers_sync_error ? (
        <div className="invoice-detail-warn invoice-detail-numbers-err-full" role="alert">
          <strong>Error Numbers (sync):</strong>{' '}
          {inv.numbers_sync_error}
        </div>
      ) : null}
      <p className="invoice-detail-dates">
        Creada: {formatDate(inv.created_at)}
        {inv.approved_at ? ` · Aprobada: ${formatDate(inv.approved_at)}` : ''}
        {inv.rejected_at ? ` · Rechazada: ${formatDate(inv.rejected_at)}` : ''}
      </p>
      {fiscal_preview_error ? (
        <div className="invoice-detail-warn" role="alert">
          <strong>No se pudo generar la vista fiscal completa:</strong> {fiscal_preview_error}
        </div>
      ) : null}

      <section className="invoice-detail-section">
        <h4>Titular y datos DIAN (factura electrónica)</h4>
        {tp ? (
          <dl className="invoice-dl">
            <div><dt>Nombre / razón social</dt><dd>{tp.name}</dd></div>
            <div><dt>Identificación</dt><dd>{tp.identificationType} {tp.identificationNumber}</dd></div>
            <div><dt>Dirección</dt><dd>{tp.address || '—'}</dd></div>
            <div><dt>Ciudad / depto</dt><dd>{[tp.city, tp.department].filter(Boolean).join(' · ')}</dd></div>
            <div><dt>Teléfono</dt><dd>{tp.phone || o.customer_phone || '—'}</dd></div>
            <div><dt>Email</dt><dd>{tp.email || o.customer_email || '—'}</dd></div>
          </dl>
        ) : (
          <p className="invoice-detail-muted">Sin vista fiscal. Datos de cliente en la orden (abajo).</p>
        )}
      </section>

      <section className="invoice-detail-section">
        <h4>Envío (entrega)</h4>
        <dl className="invoice-dl">
          <div><dt>Destinatario</dt><dd>{o.customer_name}</dd></div>
          <div><dt>Teléfono</dt><dd>{o.customer_phone || '—'}</dd></div>
          <div><dt>Dirección</dt><dd>{o.shipping_address || '—'}</dd></div>
          <div><dt>Ciudad / depto</dt><dd>{[o.shipping_city, o.shipping_department].filter(Boolean).join(' · ') || '—'}</dd></div>
          {o.shipping_municipio_dane || o.shipping_municipio_code ? (
            <div>
              <dt>Municipio (DANE)</dt>
              <dd>{[o.shipping_municipio_dane, o.shipping_municipio_code].filter(Boolean).join(' · ')}</dd>
            </div>
          ) : null}
        </dl>
      </section>

      {useDiff && hasBillingName ? (
        <section className="invoice-detail-section invoice-detail-billing-diff">
          <h4>Facturación (distinta al envío)</h4>
          <dl className="invoice-dl">
            <div><dt>Nombre facturación</dt><dd>{o.billing_customer_name}</dd></div>
            <div><dt>Identificación</dt><dd>{[o.billing_identification_type, o.billing_identification_number].filter(Boolean).join(' ')}</dd></div>
            <div><dt>Dirección</dt><dd>{o.billing_address || '—'}</dd></div>
            <div><dt>Ciudad / depto</dt><dd>{[o.billing_city, o.billing_department].filter(Boolean).join(' · ') || '—'}</dd></div>
          </dl>
        </section>
      ) : null}

      <section className="invoice-detail-section">
        <h4>Productos y conceptos</h4>
        <div className="invoice-items-wrap">
          <table className="invoice-items-table">
            <thead>
              <tr>
                <th>Descripción</th>
                <th>Cant.</th>
                <th>P. unit.</th>
                <th>Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((row, i) => (
                <tr key={i}>
                  <td>{row.name}</td>
                  <td>{row.quantity}</td>
                  <td>{formatCurrency(row.unitPrice)}</td>
                  <td>{formatCurrency(row.subtotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="invoice-detail-section">
        <h4>Totales</h4>
        <ul className="invoice-totals">
          <li><span>Subtotal</span> <span>{formatCurrency(totals.subtotal)}</span></li>
          <li><span>IVA / impuestos (orden)</span> <span>{formatCurrency(totals.tax)}</span></li>
          <li><span>Envío</span> <span>{formatCurrency(totals.shipping)}</span></li>
          <li className="total-line">
            <span><strong>Total</strong></span> <span><strong>{formatCurrency(totals.total)}</strong></span>
          </li>
        </ul>
      </section>

      {o.customer_notes ? (
        <section className="invoice-detail-section">
          <h4>Notas del pedido</h4>
          <p className="invoice-detail-notes">{o.customer_notes}</p>
        </section>
      ) : null}
    </div>
  );
}

const InvoicesManagement = () => {
  const [invoices, setInvoices] = useState([]);
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [actionLoading, setActionLoading] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [rejectReason, setRejectReason] = useState('');
  const [rejectModal, setRejectModal] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      if (activeTab === 'pending') {
        const res = await invoiceService.getPendingReview();
        const list = res?.data?.invoices ?? res?.invoices ?? [];
        setPendingInvoices(list);
      } else {
        const res = await invoiceService.getAll();
        const list = res?.data?.invoices ?? res?.invoices ?? [];
        setInvoices(list);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setMessage({ type: 'error', text: 'Error al cargar facturas' });
      setPendingInvoices([]);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handleResendToNumbers = async (invoiceId) => {
    try {
      setActionLoading(`resend-${invoiceId}`);
      setMessage({ type: '', text: '' });
      const res = await invoiceService.resendToNumbers(invoiceId);
      const inv = res?.data?.invoice ?? res?.invoice ?? {};
      setMessage({
        type: 'success',
        text:
          res?.message ||
          (inv.numbers_invoice_id
            ? 'Factura reenviada a Numbers correctamente'
            : 'Envío procesado; revisar referencia en Numbers')
      });
      fetchInvoices();
      window.dispatchEvent(new Event(ADMIN_OPS_REFRESH));
      if (detailId === invoiceId) {
        openDetail(invoiceId);
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Error al reenviar a Numbers'
      });
      fetchInvoices();
      if (detailId === invoiceId) {
        openDetail(invoiceId);
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleApprove = async (invoiceId) => {
    try {
      setActionLoading(invoiceId);
      setMessage({ type: '', text: '' });
      const res = await invoiceService.approve(invoiceId);
      if (detailId === invoiceId) {
        openDetail(invoiceId);
      }
      const inv = res?.data?.invoice ?? res?.invoice ?? {};
      const sentToNumbers = inv.sent_to_numbers === true;
      const numErr = inv.numbers_error || inv.numbers_sync_error;
      if (inv.status === 'numbers_sync_failed' || numErr) {
        setMessage({
          type: 'error',
          text: `Factura aprobada pero Numbers no aceptó el envío${numErr ? `: ${numErr}` : ''}`
        });
      } else {
        setMessage({
          type: 'success',
          text: sentToNumbers
            ? 'Factura aprobada y enviada a Numbers'
            : inv.status === 'approved_local'
              ? 'Factura aprobada (Numbers no configurado en servidor)'
              : 'Factura aprobada'
        });
      }
      fetchInvoices();
      window.dispatchEvent(new Event(ADMIN_OPS_REFRESH));
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Error al aprobar factura'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!rejectModal) return;
    const rejectedId = rejectModal.id;
    try {
      setActionLoading(rejectedId);
      setMessage({ type: '', text: '' });
      await invoiceService.reject(rejectedId, rejectReason);
      setMessage({ type: 'success', text: 'Factura rechazada' });
      setRejectModal(null);
      setRejectReason('');
      if (detailId === rejectedId) {
        openDetail(rejectedId);
      }
      fetchInvoices();
      window.dispatchEvent(new Event(ADMIN_OPS_REFRESH));
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Error al rechazar factura'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP'
    }).format(value || 0);
  };

  const openDetail = async (id) => {
    setDetailId(id);
    setDetailData(null);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const res = await invoiceService.getById(id);
      const p = res?.data ?? res;
      if (!p?.invoice) {
        setDetailError('No se pudo cargar el detalle de la factura');
        return;
      }
      setDetailData(p);
    } catch (e) {
      setDetailError(e.response?.data?.message || e.message || 'Error al cargar');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailId(null);
    setDetailData(null);
    setDetailError(null);
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending_review: { label: 'Pendiente revisión', class: 'status-pending' },
      sent_to_numbers: { label: 'Enviada a Numbers', class: 'status-sent' },
      approved_local: { label: 'Aprobada (sin sync Numbers)', class: 'status-pending' },
      numbers_sync_failed: { label: 'Error al enviar a Numbers', class: 'status-failed' },
      rejected: { label: 'Rechazada', class: 'status-rejected' }
    };
    const b = badges[status] || { label: status, class: 'status-default' };
    return <span className={`status-badge ${b.class}`}>{b.label}</span>;
  };

  const listToShow = activeTab === 'pending' ? pendingInvoices : invoices;

  const renderDetailModal = () => {
    if (!detailId) return null;
    const p = detailData;
    return (
      <div className="modal-overlay invoice-detail-overlay" onClick={closeDetail}>
        <div className="modal-content invoice-detail-modal" onClick={(e) => e.stopPropagation()}>
          <div className="invoice-detail-header">
            <h3>Detalle de factura</h3>
            <button type="button" className="invoice-detail-close" onClick={closeDetail} aria-label="Cerrar">
              ×
            </button>
          </div>
          {detailLoading && (
            <div className="invoice-detail-loading">
              <i className="fas fa-spinner fa-spin" /> Cargando…
            </div>
          )}
          {detailError && <div className="invoice-detail-err">{detailError}</div>}
          {!detailLoading && p && p.invoice && p.order && (
            <>
              <InvoiceDetailBody
                data={p}
                formatCurrency={formatCurrency}
                formatDate={formatDate}
                getStatusBadge={getStatusBadge}
              />
              {invoiceNeedsNumbersResend(p.invoice) ? (
                <div className="invoice-detail-footer-actions">
                  <button
                    type="button"
                    className="btn-resend-numbers"
                    onClick={() => handleResendToNumbers(p.invoice.id)}
                    disabled={actionLoading === `resend-${p.invoice.id}`}
                  >
                    {actionLoading === `resend-${p.invoice.id}` ? (
                      <i className="fas fa-spinner fa-spin" />
                    ) : (
                      <i className="fas fa-paper-plane" />
                    )}
                    Reenviar a Numbers
                  </button>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="invoices-management">
      <div className="container">
        <div className="section-header">
          <h2>🧾 Facturas</h2>
          <p>Revisa y aprueba facturas para enviar a Numbers (sistema contable)</p>
        </div>

        {message.text && (
          <div className={`message-banner ${message.type}`}>
            {message.text}
            <button onClick={() => setMessage({ type: '', text: '' })}>×</button>
          </div>
        )}

        <div className="tabs">
          <button
            className={`tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            <i className="fas fa-clock"></i>
            Pendientes ({pendingInvoices.length})
          </button>
          <button
            className={`tab-btn ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            <i className="fas fa-list"></i>
            Todas
          </button>
        </div>

        {loading ? (
          <div className="loading-container">
            <i className="fas fa-spinner fa-spin"></i>
            <span>Cargando facturas...</span>
          </div>
        ) : listToShow.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-file-invoice"></i>
            <h3>No hay facturas</h3>
            <p>
              {activeTab === 'pending'
                ? 'No hay facturas pendientes de revisión. Las facturas aparecen aquí cuando un cliente completa un pago.'
                : 'Aún no se han generado facturas.'}
            </p>
          </div>
        ) : (
          <div className="invoices-table-wrapper">
            <table className="invoices-table">
              <thead>
                <tr>
                  <th>Factura</th>
                  <th>Orden</th>
                  <th>Cliente</th>
                  <th>Total</th>
                  <th>Fecha</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {listToShow.map((inv) => (
                  <tr key={inv.id}>
                    <td>
                      <button
                        type="button"
                        className="invoice-link-btn"
                        onClick={() => openDetail(inv.id)}
                        title="Ver detalle de factura"
                      >
                        #{inv.id}
                      </button>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="invoice-link-btn invoice-link-ord"
                        onClick={() => openDetail(inv.id)}
                        title="Ver detalle"
                      >
                        {inv.order_number || `#${inv.order_id}`}
                      </button>
                    </td>
                    <td>
                      <div className="customer-cell">
                        <strong>{inv.customer_name || '-'}</strong>
                        <small>{inv.customer_email}</small>
                      </div>
                    </td>
                    <td>{formatCurrency(inv.order_total ?? inv.total)}</td>
                    <td>{formatDate(inv.created_at)}</td>
                    <td>
                      {getStatusBadge(inv.status)}
                      {inv.numbers_sync_error ? (
                        <div
                          className="invoice-numbers-err"
                          title={inv.numbers_sync_error}
                          style={{ fontSize: '0.75rem', color: '#8b0000', marginTop: '6px', maxWidth: '220px' }}
                        >
                          {inv.numbers_sync_error.length > 100
                            ? `${inv.numbers_sync_error.slice(0, 100)}…`
                            : inv.numbers_sync_error}
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <div className="action-buttons invoice-action-row">
                        <button
                          type="button"
                          className="btn-view"
                          onClick={() => openDetail(inv.id)}
                          disabled={detailLoading && detailId === inv.id}
                        >
                          <i className="fas fa-eye" /> Ver
                        </button>
                        {activeTab === 'pending' && inv.status === 'pending_review' && (
                          <>
                            <button
                              className="btn-approve"
                              onClick={() => handleApprove(inv.id)}
                              disabled={actionLoading === inv.id}
                            >
                              {actionLoading === inv.id ? (
                                <i className="fas fa-spinner fa-spin"></i>
                              ) : (
                                <>
                                  <i className="fas fa-check"></i> Aprobar
                                </>
                              )}
                            </button>
                            <button
                              className="btn-reject"
                              onClick={() => setRejectModal(inv)}
                              disabled={actionLoading === inv.id}
                            >
                              <i className="fas fa-times"></i> Rechazar
                            </button>
                          </>
                        )}
                        {invoiceNeedsNumbersResend(inv) ? (
                          <button
                            type="button"
                            className="btn-resend-numbers"
                            onClick={() => handleResendToNumbers(inv.id)}
                            disabled={
                              actionLoading === `resend-${inv.id}` ||
                              actionLoading === inv.id
                            }
                          >
                            {actionLoading === `resend-${inv.id}` ? (
                              <i className="fas fa-spinner fa-spin" />
                            ) : (
                              <i className="fas fa-paper-plane" />
                            )}
                            Reenviar Numbers
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {renderDetailModal()}

      {rejectModal && (
        <div className="modal-overlay" onClick={() => setRejectModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Rechazar factura</h3>
            <p>Orden: {rejectModal.order_number || `#${rejectModal.order_id}`}</p>
            <label>Motivo (opcional):</label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Ej: Datos incompletos del cliente"
              rows={3}
            />
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setRejectModal(null)}>
                Cancelar
              </button>
              <button className="btn-reject" onClick={handleReject} disabled={actionLoading}>
                {actionLoading ? <i className="fas fa-spinner fa-spin"></i> : 'Rechazar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicesManagement;
